import { motion } from 'framer-motion';
import { useState } from 'react';

export default function ContactInfoBento() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [dsgvoConsent, setDsgvoConsent] = useState(false);

  const contactMethods = [
    { title: 'E-Mail', description: 'Schreiben Sie mir jederzeit', icon: 'ri-mail-line', action: 'info-aiagents@web.de', link: 'mailto:info-aiagents@web.de' },
    { title: 'Telefon', description: 'Mo-Fr von 9 bis 18 Uhr', icon: 'ri-phone-line', action: '0162 365 1571', link: 'tel:01623651571' },
    { title: 'Antwortzeit', description: 'Schnelle Rückmeldung', icon: 'ri-time-line', action: 'Innerhalb von 24h', link: '#' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) return;
    setIsSubmitting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        params.append(key, value);
      });
      const res = await fetch('https://readdy.ai/api/form/d6bnlm2ohb161tfd7mf0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString()
      });
      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', subject: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 4000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section className="py-20 bg-brand-beige-light">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-3 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 bg-white rounded-3xl p-10 shadow-lg border border-brand-beige"
          >
            <h2 className="text-3xl font-caprasimo mb-2 text-brand-black">Nachricht senden</h2>
            <p className="text-brand-grey mb-6 text-sm">Füllen Sie das Formular aus und ich melde mich schnellstmöglich bei Ihnen.</p>

            <form onSubmit={handleSubmit} className="space-y-6" id="contact-page-form" data-readdy-form>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-brand-black mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 border-brand-beige focus:border-brand-green outline-none transition-colors text-sm bg-brand-beige-light/50"
                    placeholder="Ihr Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-black mb-2">E-Mail *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 border-brand-beige focus:border-brand-green outline-none transition-colors text-sm bg-brand-beige-light/50"
                    placeholder="ihre@email.de"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-brand-black mb-2">Unternehmen</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-brand-beige focus:border-brand-green outline-none transition-colors text-sm bg-brand-beige-light/50"
                    placeholder="Ihr Unternehmen"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-black mb-2">Telefon</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-brand-beige focus:border-brand-green outline-none transition-colors text-sm bg-brand-beige-light/50"
                    placeholder="Ihre Telefonnummer"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-black mb-2">Betreff *</label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 border-brand-beige focus:border-brand-green outline-none transition-colors text-sm bg-brand-beige-light/50"
                >
                  <option value="">Bitte wählen...</option>
                  <option value="Neue Webseite">Neue Webseite von Grund auf</option>
                  <option value="Premium Redesign">Premium Redesign</option>
                  <option value="AI Chatbots & Automatisierung">AI Chatbots &amp; Automatisierung</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-black mb-2">Nachricht *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 rounded-lg border-2 border-brand-beige focus:border-brand-green outline-none transition-colors text-sm resize-none bg-brand-beige-light/50"
                  placeholder="Erzählen Sie mir von Ihrem Projekt..."
                />
                <p className="text-xs text-brand-grey mt-1">{formData.message.length}/500 Zeichen</p>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="dsgvo-consent-page"
                  checked={dsgvoConsent}
                  onChange={(e) => setDsgvoConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-brand-green cursor-pointer flex-shrink-0"
                />
                <label htmlFor="dsgvo-consent-page" className="text-xs text-brand-grey leading-relaxed cursor-pointer select-none">
                  Ich stimme zu, dass meine Angaben aus dem Kontaktformular zur Beantwortung meiner Anfrage erhoben und verarbeitet werden. Die Daten werden nach abgeschlossener Bearbeitung Ihrer Anfrage gelöscht. Hinweis: Sie können Ihre Einwilligung jederzeit für die Zukunft per E-Mail an info-aiagents@web.de widerrufen. Detaillierte Informationen zum Umgang mit Nutzerdaten finden Sie in unserer{' '}
                  <a href="/datenschutz" className="text-brand-green underline hover:text-brand-green-light transition-colors">
                    Datenschutzerklärung
                  </a>.
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !dsgvoConsent}
                className="w-full bg-brand-green hover:bg-brand-green-light text-white py-4 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap shadow-lg"
              >
                {isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-brand-green/10 text-brand-green px-4 py-3 rounded-lg text-sm border border-brand-green/20">
                  Nachricht erfolgreich gesendet! Ich melde mich schnellstmöglich bei Ihnen.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm border border-red-200">
                  Fehler beim Senden. Bitte versuchen Sie es erneut.
                </div>
              )}
            </form>

            <div className="border-t border-brand-beige pt-8 mt-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-brand-beige-light rounded-xl">
                  <i className="ri-time-line text-xl text-brand-green"></i>
                </div>
                <h3 className="text-lg font-caprasimo text-brand-black">Erreichbarkeit</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-6 text-sm">
                <div>
                  <div className="font-semibold mb-1 text-brand-black">Montag – Freitag</div>
                  <div className="text-brand-grey">9:00 – 18:00 Uhr</div>
                </div>
                <div>
                  <div className="font-semibold mb-1 text-brand-black">Samstag</div>
                  <div className="text-brand-grey">Nach Vereinbarung</div>
                </div>
                <div>
                  <div className="font-semibold mb-1 text-brand-black">Sonntag</div>
                  <div className="text-brand-grey">Geschlossen</div>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="space-y-6">
            {contactMethods.map((method, index) => (
              <motion.a
                key={index}
                href={method.link}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="group block bg-white rounded-2xl p-8 hover:shadow-2xl transition-all cursor-pointer border border-brand-beige hover:border-brand-green"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-brand-beige-light rounded-xl mb-6 group-hover:bg-brand-green group-hover:scale-110 transition-all">
                  <i className={`${method.icon} text-2xl text-brand-green group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-caprasimo mb-2 text-brand-black group-hover:text-brand-green transition-colors">
                  {method.title}
                </h3>
                <p className="text-brand-grey mb-4 text-sm">{method.description}</p>
                <div className="text-brand-green font-semibold flex items-center gap-2 text-sm">
                  {method.action}
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </div>
              </motion.a>
            ))}

            {/* Benjamin Reick Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-brand-green rounded-2xl p-8 text-white"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-xl mb-6">
                <i className="ri-user-smile-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-caprasimo mb-2">Benjamin Reick</h3>
              <p className="text-white/80 text-sm mb-4">Ihr persönlicher Ansprechpartner für Webdesign, AI Chatbots & SEO</p>
              <div className="space-y-2 text-sm">
                <a href="tel:01623651571" className="flex items-center gap-2 text-white/90 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-phone-line"></i> 0162 365 1571
                </a>
                <a href="mailto:info-aiagents@web.de" className="flex items-center gap-2 text-white/90 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-mail-line"></i> info-aiagents@web.de
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
